import { isAxiosError } from 'axios'
import { toast } from 'sonner'
import { API_ERROR_MESSAGE } from '@/constants/api'

/**
 * Centralized toast utility for consistent notifications
 */

/** Show a success toast */
export function showSuccess(message: string) {
  toast.success(message)
}

/** Show an error toast with backend message or fallback */
export function showError(error?: unknown, fallbackMessage?: string) {
  const message =
    extractErrorMessage(error) || fallbackMessage || API_ERROR_MESSAGE
  toast.error(message)
}

/** Show a warning toast */
export function showWarning(message: string) {
  toast.warning(message)
}

/** Show an info toast */
export function showInfo(message: string) {
  toast.info(message)
}

/** Show a loading toast that can be updated */
export function showLoading(message: string) {
  return toast.loading(message)
}

/** Dismiss a specific toast or all toasts */
export function dismissToast(toastId?: string | number) {
  toast.dismiss(toastId)
}

/**
 * Extract error message from various error formats
 *
 * Exported as well as used by `showError`, because a batch reports per-lead
 * failures in a list rather than a toast, and those messages should be the
 * same backend text a single failure would have shown.
 */
export function extractErrorMessage(error: unknown): string | null {
  if (!error) return null

  // String error
  if (typeof error === 'string') {
    return error
  }

  // Check Axios response first — the backend message is more useful
  // than the generic Axios "Request failed with status code 400"
  if (
    isAxiosError(error) &&
    typeof error.response?.data?.message === 'string'
  ) {
    return error.response.data.message
  }

  // Fallback to Error.message for non-API errors
  if (error instanceof Error) {
    return error.message
  }

  return null
}

export { toast }
